import axios, {AxiosError} from "axios";
import {get, isString} from "lodash";
import {useCallback, useEffect} from "react";
import {toast} from "sonner";
import {useNavigate} from "react-router-dom";
import {axiosReachInstance} from "./ReachApiManager";

type ValidationErrorItem = {msg?: unknown};

const VALUE_ERROR_PREFIX = /^(Value|Assertion) error,\s*/;

const extractValidationMessages = (detail: unknown[]): string | undefined => {
  const messages = detail
    .map((item) => (item as ValidationErrorItem)?.msg)
    .filter(isString)
    .map((msg) => msg.replace(VALUE_ERROR_PREFIX, ""));
  return messages.length ? messages.join("\n") : undefined;
};

const extractErrorMessage = (errorData: unknown): string | undefined => {
  if (isString(errorData)) return errorData;
  if (errorData && typeof errorData === "object") {
    const detail = (errorData as {detail?: unknown}).detail;
    if (isString(detail)) return detail;
    if (Array.isArray(detail)) return extractValidationMessages(detail);
  }
  return undefined;
};

const ReachApiExceptionManager = () => {
  const navigate = useNavigate();

  const notificationHandler = useCallback((error: AxiosError) => {
    const errorMessage = extractErrorMessage(get(error, "response.data"));
    const fallback = error.response ? "Something went wrong." : "Network connection is lost.";
    toast.error(errorMessage || fallback);
  }, []);

  const errorHandler = useCallback(
    (error: AxiosError) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);

      const status = error.response?.status;
      if (status === 401) {
        localStorage.removeItem("agentToken");
        navigate("/login");
        return Promise.reject(error);
      }
      if (status === 403) {
        toast.error("You don't have permission to access Reach.");
        window.location.href = import.meta.env.VITE_SPRING_HOST;
        return Promise.reject(error);
      }

      if (axios.isCancel(error)) return Promise.reject(error);

      notificationHandler(error);
      return Promise.reject(error);
    },
    [navigate, notificationHandler]
  );

  useEffect(() => {
    const interceptorId = axiosReachInstance.interceptors.response.use(undefined, errorHandler);
    return () => {
      axiosReachInstance.interceptors.response.eject(interceptorId);
    };
  }, [errorHandler]);

  return null;
};

export default ReachApiExceptionManager;
